/**
 * SessionTimeline Component
 *
 * Displays a vertical timeline of session events with EventCard components
 */

import React from 'react';
import { ParsedEvent } from '../../types/session';
import { EventCard } from '../ui/EventCard';

interface SessionTimelineProps {
  events: ParsedEvent[];
  isLoading?: boolean;
  sessionStatus?: string;
}

export const SessionTimeline: React.FC<SessionTimelineProps> = ({
  events,
  isLoading = false,
  sessionStatus,
}) => {
  if (isLoading) {
    return (
      <div className="flex justify-center items-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  if (events.length === 0) {
    // Show different message based on session status
    const isRunning = sessionStatus === 'running';

    return (
      <div className="text-center py-12">
        <svg
          className="mx-auto h-12 w-12 text-gray-400"
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
          aria-hidden="true"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"
          />
        </svg>
        <h3 className="mt-2 text-sm font-medium text-gray-900">
          {isRunning ? 'Initializing agent...' : 'No events yet'}
        </h3>
        <p className="mt-1 text-sm text-gray-500">
          {isRunning
            ? 'Setting up the browser and preparing to execute the task'
            : 'Events will appear here once the job starts executing'}
        </p>
      </div>
    );
  }

  return (
    <div className="relative">
      {/* Timeline Line */}
      <div
        className="absolute left-2.5 top-0 bottom-0 w-0.5 bg-gray-200"
        aria-hidden="true"
      ></div>

      {/* Event Cards */}
      <div className="space-y-4">
        {events.map((event, index) => (
          <div key={event.id || index} className="relative pl-8">
            {/* Timeline Dot */}
            <div
              className="absolute left-0 top-2 w-5 h-5 bg-blue-600 rounded-full border-2 border-white shadow"
              aria-hidden="true"
            />

            {/* Event Card */}
            <EventCard event={event} index={index} />
          </div>
        ))}
      </div>

      {/* Summary */}
      <div className="mt-6 pt-6 border-t border-gray-200 space-y-2">
        <div className="flex items-center justify-between text-sm">
          <span className="font-medium text-gray-700">Total Events:</span>
          <span className="text-gray-900">{events.length}</span>
        </div>
        {(() => {
          // Get total tokens from final_response event
          const finalEvent = events.find((e) => e.type === 'final_response');
          const totalTokens = finalEvent?.totalTokenCount;

          if (totalTokens !== undefined) {
            return (
              <div className="flex items-center justify-between text-sm">
                <span className="font-medium text-gray-700">Total Tokens:</span>
                <span className="text-gray-900">{totalTokens.toLocaleString()}</span>
              </div>
            );
          }
          return null;
        })()}
        {(() => {
          // Calculate total time from first to last event
          const timestamps = events
            .map((e) => e.timestamp)
            .filter((t): t is number => t !== undefined)
            .sort((a, b) => a - b);

          if (timestamps.length >= 2) {
            const totalSeconds = timestamps[timestamps.length - 1] - timestamps[0];
            const minutes = Math.floor(totalSeconds / 60);
            const seconds = Math.floor(totalSeconds % 60);

            return (
              <div className="flex items-center justify-between text-sm">
                <span className="font-medium text-gray-700">Total Time:</span>
                <span className="text-gray-900">
                  {minutes > 0 ? `${minutes}m ${seconds}s` : `${seconds}s`}
                </span>
              </div>
            );
          }
          return null;
        })()}
      </div>
    </div>
  );
};
